import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useFiscalErrors } from '@/hooks/useFiscal';
import { getDocumentTypeName } from '@/lib/fiscal/documentTypes';
import { formatDateTime } from '@/lib/format';

/** Acción recomendada por estado fiscal del documento. */
function recommendedAction(status: string): string {
  switch (status) {
    case 'REJECTED':
      return 'Corregir la información y emitir un nuevo comprobante.';
    case 'ERROR':
      return 'Revisar la causa del error y reintentar el envío.';
    case 'RETRY':
      return 'Reintento de envío en curso.';
    default:
      return 'Revisar el detalle del comprobante.';
  }
}

function causeFor(doc: { rejection_reason: string | null; dgii_status: string | null }): string {
  if (doc.rejection_reason) return doc.rejection_reason;
  if (doc.dgii_status) return `Estado DGII: ${doc.dgii_status}`;
  return 'Causa no especificada.';
}

export default function FiscalErrorsPage() {
  const { errorDocuments, errors, loading, error, reload } = useFiscalErrors();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const handleRetry = async (docId: string) => {
    setBusyId(docId);
    try {
      const { error: e } = await supabase.rpc('retry_fiscal_document', { p_doc_id: docId });
      if (e) throw e;
      showToast('Documento marcado para reintento.');
      reload();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'No se pudo reintentar el documento');
    } finally {
      setBusyId(null);
    }
  };

  const total = errorDocuments.length + errors.length;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            Centro de errores fiscales
          </h1>
          <p className="mt-1 text-foreground-600">
            Comprobantes rechazados o con fallos de procesamiento, con la causa real del rechazo.
          </p>
        </div>
        <Link
          to="/fiscal"
          className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
        >
          <i className="ri-arrow-left-line" /> Volver al panel
        </Link>
      </div>

      {error && (
        <div className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-600" />
          <p className="text-sm text-foreground-700">{error}</p>
          <button
            onClick={reload}
            className="ml-auto text-sm font-medium text-red-700 hover:underline cursor-pointer whitespace-nowrap"
          >
            Reintentar
          </button>
        </div>
      )}

      {loading ? (
        <div className="mt-10 flex items-center justify-center py-20">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : total === 0 ? (
        <div className="mt-8 rounded-lg border border-dashed border-background-300 p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto">
            <i className="ri-checkbox-circle-line text-3xl" />
          </div>
          <p className="mt-4 font-heading font-semibold text-foreground-950">
            Sin errores fiscales registrados
          </p>
          <p className="mt-1 text-sm text-foreground-600">
            Cuando la DGII rechace un comprobante, aparecerá aquí con su causa y la acción
            recomendada.
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {/* Documentos en estado de error */}
          {errorDocuments.length > 0 && (
            <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-200">
                <h2 className="font-heading font-semibold text-foreground-950 flex items-center gap-2">
                  <i className="ri-file-warning-line text-red-600" />
                  Comprobantes en estado de error
                </h2>
              </div>
              <ul className="divide-y divide-background-100">
                {errorDocuments.map((d) => (
                  <li key={d.id} className="px-5 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-foreground-950">
                            {d.e_ncf ?? 'Sin e-NCF'}
                          </span>
                          <span className="rounded-full bg-red-100 text-red-800 px-2 py-0.5 text-xs font-semibold">
                            {d.fiscal_status}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-foreground-500">
                          {getDocumentTypeName(d.document_type)} · {formatDateTime(d.created_at)}
                        </p>
                        <div className="mt-3 rounded-md bg-red-50 border border-red-100 px-3 py-2.5">
                          <p className="text-xs font-semibold text-red-800">Causa</p>
                          <p className="mt-0.5 text-sm text-foreground-700">{causeFor(d)}</p>
                        </div>
                        <div className="mt-2 rounded-md bg-background-100 px-3 py-2.5">
                          <p className="text-xs font-semibold text-foreground-700">Acción recomendada</p>
                          <p className="mt-0.5 text-sm text-foreground-700">
                            {recommendedAction(d.fiscal_status)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRetry(d.id)}
                        disabled={busyId === d.id}
                        className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer shrink-0"
                      >
                        <i className="ri-refresh-line" />
                        {busyId === d.id ? 'Reintentando...' : 'Reintentar'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Registro de errores */}
          {errors.length > 0 && (
            <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-200">
                <h2 className="font-heading font-semibold text-foreground-950 flex items-center gap-2">
                  <i className="ri-error-warning-line text-amber-600" />
                  Registro de errores
                </h2>
              </div>
              <ul className="divide-y divide-background-100">
                {errors.map((e) => (
                  <li key={e.id} className="px-5 py-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-foreground-950">
                        {e.e_ncf ?? 'Sin e-NCF'}
                      </span>
                      {e.error_code && (
                        <span className="rounded-full bg-background-100 text-foreground-700 px-2 py-0.5 text-xs font-mono">
                          {e.error_code}
                        </span>
                      )}
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                          e.status === 'OPEN'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-emerald-100 text-emerald-800'
                        }`}
                      >
                        {e.status}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-foreground-500">
                      {formatDateTime(e.created_at)} · {e.retry_count} reintentos
                    </p>
                    {e.error_message && (
                      <p className="mt-2 text-sm text-foreground-700">{e.error_message}</p>
                    )}
                    {e.recommended_action && (
                      <p className="mt-1 text-sm text-foreground-600">
                        <span className="font-medium">Acción:</span> {e.recommended_action}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {toast && (
        <div className="fixed bottom-20 lg:bottom-6 left-1/2 -translate-x-1/2 z-[60] rounded-md bg-foreground-950 text-background-50 px-4 py-2.5 text-sm font-medium shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}